// src/hooks/useApkUpdate.ts
//
// iSentinel360 v0.4.5 — APK auto-update state management hook.
//
// What it does:
//   - On mount, silently calls window.Android.checkForApkUpdate()
//   - Exposes the result via the `update` state
//   - Provides `install()` to trigger the native download+install flow
//   - Provides `dismiss()` to hide the banner for the current session
//     only (sessionStorage-based; the banner reappears next launch
//     until the user actually updates)
//   - Exposes the install state (installing, error) so the modal
//     can show progress
//
// v0.4.5 change: `check()` now resets `dismissedThisSession`. When the
// user explicitly clicks "Check for updates" (from the hamburger
// menu), they want to see the current state even if they had
// previously dismissed the banner earlier in the session. The
// auto-check on mount does NOT reset (passive check, don't
// re-show what the user already dismissed).
//
// Why sessionStorage, not localStorage: the user said "dismiss just
// hides for current session" — sessionStorage is the right primitive.
// Cleared when the tab/app is closed.
//
// How to use:
//   const { update, dismiss, install, installing, installError,
//           openUnknownAppsSettings, checking, check } = useApkUpdate()
//
// This hook is a no-op in desktop Chrome (no window.Android). The
// `update` state stays null, the banner never renders, the menu item
// just no-ops. This is intentional — desktop users get updates via
// your normal browser refresh, not via this channel.

import { useCallback, useEffect, useState } from 'react';

export interface ApkUpdateInfo {
  updateAvailable: boolean;
  currentVersionCode: number;
  currentVersionName: string;
  versionCode: number;
  versionName: string;
  downloadUrl: string;
  releaseNotes: string;
  mandatory: boolean;
  error?: string;
}

export interface InstallResult {
  success: boolean;
  error?: string;
  needsUnknownAppsPermission?: boolean;
}

interface AndroidBridge {
  checkForApkUpdate: (callbackName: string) => void;
  downloadAndInstallApk: (downloadUrl: string, callbackName: string) => void;
  openUnknownAppsSettings: () => void;
}

declare global {
  interface Window {
    Android?: AndroidBridge;
  }
}

const SESSION_DISMISS_KEY = 'is360_apkUpdateDismissedSession';
const CHECK_CALLBACK = '__is360_apkUpdateCheckResult';
const INSTALL_CALLBACK = '__is360_apkUpdateInstallResult';

export function useApkUpdate() {
  const [update, setUpdate] = useState<ApkUpdateInfo | null>(null);
  const [checking, setChecking] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const [dismissedThisSession, setDismissedThisSession] = useState<boolean>(
    () => sessionStorage.getItem(SESSION_DISMISS_KEY) === 'true',
  );

  const check = useCallback(() => {
    if (!window.Android?.checkForApkUpdate) {
      // Not running in the APK WebView — no-op. The hook still
      // works (returns null update) so PWA components render
      // normally in desktop Chrome.
      return;
    }
    // v0.4.5: reset the dismiss flag. The user explicitly asked
    // to check, so they want to see the result even if they had
    // dismissed the banner earlier in this session. The auto-
    // check on mount (below) does NOT call this — it relies on
    // the initial value of `dismissedThisSession` from sessionStorage.
    setDismissedThisSession(false);
    setChecking(true);
    (window as unknown as Record<string, unknown>)[CHECK_CALLBACK] = (
      result: ApkUpdateInfo,
    ) => {
      setUpdate(result);
      setChecking(false);
    };
    window.Android.checkForApkUpdate(CHECK_CALLBACK);
  }, []);

  // Auto-check on mount
  useEffect(() => {
    check();
    // check is stable (useCallback with empty deps), so this
    // effect only runs once.
  }, [check]);

  const dismiss = useCallback(() => {
    setDismissedThisSession(true);
    sessionStorage.setItem(SESSION_DISMISS_KEY, 'true');
  }, []);

  const install = useCallback(
    (downloadUrl: string): Promise<InstallResult> => {
      return new Promise((resolve) => {
        if (!window.Android?.downloadAndInstallApk) {
          resolve({ success: false, error: 'bridge_unavailable' });
          return;
        }
        if (!downloadUrl) {
          resolve({ success: false, error: 'no_download_url' });
          return;
        }
        setInstalling(true);
        setInstallError(null);
        (window as unknown as Record<string, unknown>)[INSTALL_CALLBACK] = (
          result: InstallResult,
        ) => {
          setInstalling(false);
          if (!result.success) {
            setInstallError(result.error ?? 'unknown_error');
          } else {
            // Successful hand-off to installer — clear the dismiss
            // flag so the banner state is fresh after the update.
            sessionStorage.removeItem(SESSION_DISMISS_KEY);
            setDismissedThisSession(false);
          }
          resolve(result);
        };
        window.Android.downloadAndInstallApk(downloadUrl, INSTALL_CALLBACK);
      });
    },
    [],
  );

  const openUnknownAppsSettings = useCallback(() => {
    window.Android?.openUnknownAppsSettings();
  }, []);

  return {
    update: dismissedThisSession ? null : update,
    checking,
    installing,
    installError,
    check,
    dismiss,
    install,
    openUnknownAppsSettings,
  };
}
